<template>
	<span v-if="item.type === 'button'" class="tb-item tb-button" :class="{ disabled: disabled }" v-bind="tooltipBind" @mousedown="mouseDown" @click="doAction"><sym-icon v-if="item.icon" :name="item.icon"></sym-icon><span v-if="item.title" class="tb-label">{{ item.title }}</span></span>
	<span v-else-if="item.type === 'switch'" class="tb-item tb-switch" :class="{ disabled: disabled, selected:selected }" v-bind="tooltipBind" @click="doAction"><sym-icon v-if="item.icon" :name="item.icon"></sym-icon><span v-if="item.title" class="tb-label">{{ item.title }}</span></span>
	<anchor-element v-else-if="item.type === 'menu'" tag="span" :chain="chain" v-bind="tooltipBind" @mousedown="mouseDown" @click="openMenu" class="tb-item tb-menu"><sym-icon v-if="item.icon" :name="item.icon"></sym-icon><span v-if="item.title" class="tb-label">{{ item.title }}</span><sym-icon name="menu-drop"></sym-icon></anchor-element>
	<anchor-element v-else-if="item.type === 'palette'" tag="span" :chain="chain" v-bind="tooltipBind" @mousedown="mouseDown" @click="openMenu" class="tb-item tb-palette"><sym-icon v-if="item.icon" :name="item.icon"></sym-icon><span v-if="item.title" class="tb-label">{{ item.title }}</span></anchor-element>
	<boat-element :chain="chain" tag="div" class="tb-drop-menu">
		<component v-if="item.type === 'palette'" :is="(item as ToolbarPalette).component" @action="paletteAction($event, item as ToolbarPalette)"></component>
		<tool-menu-item v-else v-for="it in menuItems" :key="it.id" :item="it" dir="right" @action="menuItemAction"></tool-menu-item>
		<!--span v-else v-for="it in (item as ToolbarMenu).items" :key="it.id" class="tb-menu-item" @mousedown="mouseDown" @click="doMenuItemAction($event, it)"><sym-icon v-if="it.icon" :name="it.icon"></sym-icon><span v-bind="menuItemBind(it)">{{ it.title }}</span></span-->
	</boat-element>
</template>

<script setup lang="ts">
import {computed, ref, onMounted} from "vue";
import type {HasArgument, ToolbarElement, ToolbarMenu, ToolbarMenuItem, ToolbarMenuItems, ToolbarPalette, ToolbarSwitch} from "@t/ui/Toolbar.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {createChain} from "@/ui/paneltree/PanelTree.ts";
import ToolMenuItem from "@/ui/toolbar/ToolMenuItem.vue";

const props = defineProps<{
	item: ToolbarElement
}>();

const emit = defineEmits(["action"]);

const chain = createChain({
	rule: {
		ah: 0, av: 100,
		bh: 0, bv: 0,
		x: -1, y: -2
	}
}, "html-toolbar-menu-"+props.item.id);

const menuItems = ref<ToolbarMenuItems>([]);

const tooltipBind = computed(() => {
	return !!props.item.tooltip ? {title: props.item.tooltip} : {};
});

const disabled = computed<boolean>(() => !!props.item.disabled);
const selected = computed<boolean>(() => !!(props.item as ToolbarSwitch).on);

function mouseDown(e:MouseEvent) {
	e.preventDefault();		// without preventing this click selection was lost
}

function doAction(e:MouseEvent) {
	e.preventDefault();
	e.stopPropagation();
	if (disabled.value) return;
	emit("action", props.item.id, (props.item as HasArgument).arg);
}

function menuItemAction(id: string, arg?: any) {
	chain.hide();
	emit("action", id, arg);
}

function paletteAction(color:string, p:ToolbarPalette) {
	if (!!p.disabled) return;
	chain.hide();
	emit("action", p.id, color);
}

function openMenu(e:MouseEvent):void {
	e.preventDefault();
	e.stopPropagation();
	chain.toggle();
}

onMounted(() => {
	if (props.item.type === 'menu') {
		const item = (props.item as ToolbarMenu)
		if (Array.isArray(item.items)) menuItems.value.push(...item.items);
		else if (typeof item.items === "function") {
			item.items().then(items => {
				menuItems.value.push(...items);
			});
		}
	}
});
</script>

<style>
.tb-item {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: baseline;
	column-gap: 2px;
	padding: 4px;

	& > .fi {
		min-width: 20px;
	}
}

.tb-button, .tb-switch {
	background-color: var(--toolbar-button-bg);
	color: var(--toolbar-button-fg);

	&:not(.disabled):hover {
		background-color: var(--toolbar-button-bg-hover);
		color: var(--toolbar-button-fg-hover);
	}

	&:not(.disabled):active {
		background-color: var(--toolbar-button-bg-active);
		color: var(--toolbar-button-fg-active);
	}
}
.tb-switch.selected {
	background-color: var(--toolbar-button-bg-select);
	color: var(--toolbar-button-fg-select);

	&:not(.disabled):hover {
		background-color: var(--toolbar-button-bg-select-hover);
		color: var(--toolbar-button-fg-select);
	}

	&:not(.disabled):active {
		background-color: var(--toolbar-button-bg-select-hover);
		color: var(--toolbar-button-fg-select);
	}
}

.tb-label {
	font-size: 12px;
	line-height: 1em;
}

.tb-menu, .tb-palette {
	background-color: var(--toolbar-button-bg);
	color: var(--toolbar-button-fg);
	column-gap: 0;

	&:not(.disabled):hover {
		background-color: var(--toolbar-button-bg-hover);
		color: var(--toolbar-button-fg-hover);
	}

	&:not(.disabled):active {
		background-color: var(--toolbar-button-bg-active);
		color: var(--toolbar-button-fg-active);
	}

	& .tb-label {
		font-size: 14px;
		font-weight: bold;
	}
}
.tb-menu {
	padding-right: 0;
}

.tb-drop-menu {
	z-index: var(--drop-z-index);
	display: flex;
	flex-direction: column;
	border-bottom-left-radius: var(--input-radius);
	border-bottom-right-radius: var(--input-radius);
	cursor: default;
	user-select: none;
	-webkit-user-select: none;
	background-color: var(--toolbar-bg);
	color: var(--toolbar-fg);
	border: 1px solid var(--menu-border-color);
	box-shadow: var(--panel-shadow);
	padding: 4px 0;
}

</style>